/*
 * Announcement Bar — vanilla rewrite of orion-v3.0's jQuery + js-
 * cookie version. Handles:
 *
 *   Dismissal       Close-button click sets `announcement-bar=dismissed`
 *                   in document.cookie with Expires = N days from now
 *                   (N comes from the bar's `data-reappear-after`
 *                   attribute, set by Liquid from the section's
 *                   `reappear_after` setting). The bar's wrapper is
 *                   hidden and `--announcement-height` is zeroed so
 *                   the navbar slides up to where the bar was.
 *
 *   Desktop fade    Per `data-autoplay-speed` (seconds) the visible
 *                   announcement cross-fades to the next. All items
 *                   sit absolutely positioned in the same slot —
 *                   only the `.is-active` one is opaque. Pure CSS
 *                   transitions; JS only toggles classes.
 *
 *   Mobile marquee  At < 768px the items list is duplicated once
 *                   so the @keyframes marquee animation can scroll
 *                   the doubled list halfway and repeat seamlessly.
 *                   Class `.announcement-bar__items--marquee` flips
 *                   the animation on.
 *
 *   Resize          Crossing the mobile breakpoint flips the mode.
 *
 * The bar is loaded from the header section, so this script runs
 * once at page load and binds each `[data-announcement-bar]` it
 * finds. Re-runs on `shopify:section:load` for customizer edits.
 *
 * Replaces the prior H3.0 announcement bar (which was a custom
 * element extending the Component base class) — the prior file
 * was consumed only by the now-orphaned `header-announcements`
 * section that Stage 7 of the header port deletes.
 */

const SELECTOR = '[data-announcement-bar]';
const INSTANCE_KEY = '_announcementBarInstance';
const COOKIE_NAME = 'announcement-bar';
const MOBILE_BREAKPOINT = 768;

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

class AnnouncementBar {
  constructor(root) {
    this.root = root;
    this.wrapper = root.closest('.announcement-bar__wrapper') || root;
    this.itemsList = root.querySelector('.announcement-bar__items');
    this.closeBtn = root.querySelector('.announcement-bar__close');
    this.autoplaySpeed = (parseFloat(root.dataset.autoplaySpeed) || 5) * 1000;
    this.reappearAfter = parseFloat(root.dataset.reappearAfter) || 7;

    this.originalItems = this.itemsList
      ? Array.from(this.itemsList.children).map((el) => el.cloneNode(true))
      : [];

    this.fadeTimer = null;
    this.activeIndex = 0;
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    this.resizeHandler = null;

    this.init();
  }

  init() {
    if (readCookie(COOKIE_NAME) === 'dismissed') {
      this.dismiss();
      return;
    }

    document.body.classList.add('announcement-bar--show');

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        writeCookie(COOKIE_NAME, 'dismissed', this.reappearAfter);
        this.dismiss();
      });
    }

    this.applyMode();

    this.resizeHandler = () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (wasMobile !== this.isMobile) {
        this.stopFade();
        this.resetItems();
        this.applyMode();
      }
    };
    window.addEventListener('resize', this.resizeHandler);

    this.root[INSTANCE_KEY] = this;
  }

  applyMode() {
    if (!this.itemsList || this.originalItems.length === 0) return;

    if (this.isMobile) {
      this.initMarquee();
    } else {
      this.resetItems();
      this.initFade();
    }
  }

  resetItems() {
    if (!this.itemsList) return;
    this.itemsList.innerHTML = '';
    this.originalItems.forEach((item) => {
      this.itemsList.appendChild(item.cloneNode(true));
    });
    this.itemsList.classList.remove('announcement-bar__items--marquee');
  }

  /*
   * Mobile marquee — duplicate the items list so the CSS
   * `@keyframes marquee` animation can shift the wrapper -50%
   * and have the second half take over seamlessly. Without the
   * duplication the animation would visibly snap back to start.
   */
  initMarquee() {
    if (!this.itemsList || this.originalItems.length === 0) return;
    this.itemsList.innerHTML = '';
    this.originalItems.forEach((item) => this.itemsList.appendChild(item.cloneNode(true)));
    this.originalItems.forEach((item) => this.itemsList.appendChild(item.cloneNode(true)));
    this.itemsList.classList.add('announcement-bar__items--marquee');
  }

  /*
   * Desktop fade — only the .is-active item is opaque (CSS rule).
   * Every autoplaySpeed ms we rotate which item carries the
   * class. Skip when there's only one announcement: nothing to
   * cycle through.
   */
  initFade() {
    if (!this.itemsList) return;
    const items = Array.from(this.itemsList.children);
    if (items.length < 2) {
      items.forEach((el) => el.classList.add('is-active'));
      return;
    }

    items.forEach((el, i) => {
      el.classList.toggle('is-active', i === 0);
    });
    this.activeIndex = 0;

    this.fadeTimer = setInterval(() => {
      const all = Array.from(this.itemsList.children);
      if (all.length < 2) return;
      all[this.activeIndex]?.classList.remove('is-active');
      this.activeIndex = (this.activeIndex + 1) % all.length;
      all[this.activeIndex]?.classList.add('is-active');
    }, this.autoplaySpeed);
  }

  stopFade() {
    if (this.fadeTimer) {
      clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  dismiss() {
    document.body.classList.remove('announcement-bar--show');
    document.body.classList.add('announcement-bar--hide');
    if (this.wrapper) {
      this.wrapper.style.display = 'none';
    }
    document.documentElement.style.setProperty('--announcement-height', '0px');
    this.stopFade();
  }

  destroy() {
    this.stopFade();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new AnnouncementBar(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
